import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Header';
import api from '../../services/apiService';
import { useNotification } from '../NotificationProvider';
import './AssignManagerCoach.scss';

const CLUB_SECRETARY_ROLE = 'Club Secretary';

function AssignClubSecretary() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState(new Set());
  const [initialTeamIds, setInitialTeamIds] = useState(new Set());

  const [userSearchMode, setUserSearchMode] = useState('search');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loadingManagerTeams, setLoadingManagerTeams] = useState(false);

  const [managerData, setManagerData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  const secretaryReady =
    userSearchMode === 'search'
      ? !!selectedManager
      : !!(managerData.first_name.trim() && managerData.last_name.trim() && managerData.phone.trim());

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_all_teams.php`);
        if (response.data.success) {
          setTeams(response.data.teams || []);
        } else {
          showError(response.data.message || 'Failed to load teams');
        }
      } catch (err) {
        console.error(err);
        showError('Failed to load teams');
      } finally {
        setTeamsLoading(false);
      }
    };

    loadTeams();
  }, [showError]);

  const filteredTeams = useMemo(() => {
    const term = teamSearch.trim().toLowerCase();
    let list = teams.filter((t) => t.type !== 'friendly');
    if (term) {
      list = list.filter((t) => {
        const haystack = `${t.team_name} ${t.type || ''} ${t.age_group || ''} ${t.league_name || ''}`.toLowerCase();
        return haystack.includes(term);
      });
    }
    return list.sort((a, b) => a.team_name.localeCompare(b.team_name));
  }, [teams, teamSearch]);

  const loadManagerTeams = async (managerId) => {
    setLoadingManagerTeams(true);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/admin/get_manager_teams.php?manager_id=${managerId}`
      );
      if (response.data.success) {
        const teamIds = new Set((response.data.teams || []).map((team) => String(team.team_id)));
        setSelectedTeamIds(new Set(teamIds));
        setInitialTeamIds(new Set(teamIds));
      } else {
        setSelectedTeamIds(new Set());
        setInitialTeamIds(new Set());
      }
    } catch (err) {
      console.error(err);
      setSelectedTeamIds(new Set());
      setInitialTeamIds(new Set());
    } finally {
      setLoadingManagerTeams(false);
    }
  };

  const searchManagers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/search_managers.php?term=${encodeURIComponent(searchTerm)}`
      );
      if (response.data.success) {
        setSearchResults(response.data.managers || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error(err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSearch = (e) => {
    const term = e.target.value;
    setUserSearchTerm(term);
    if (term.length >= 2) {
      searchManagers(term);
    } else {
      setSearchResults([]);
    }
  };

  const resetTeamSelection = () => {
    setSelectedTeamIds(new Set());
    setInitialTeamIds(new Set());
    setTeamSearch('');
    setAssignResult(null);
  };

  const clearUserSelection = () => {
    setSelectedManager(null);
    setUserSearchTerm('');
    setManagerData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    });
    setSearchResults([]);
    resetTeamSelection();
  };

  const selectManager = async (manager) => {
    setSelectedManager(manager);
    setManagerData({
      first_name: manager.first_name,
      last_name: manager.last_name,
      email: manager.email || '',
      phone: manager.phone ? `0${manager.phone}` : '',
    });
    setUserSearchTerm(`${manager.first_name} ${manager.last_name} (${manager.email})`);
    setSearchResults([]);
    setAssignResult(null);
    await loadManagerTeams(manager.manager_id);
  };

  const handleManagerChange = (e) => {
    const { name, value } = e.target;
    setManagerData((prev) => ({ ...prev, [name]: value }));
    setAssignResult(null);
  };

  const toggleTeamSelection = (teamId) => {
    const id = String(teamId);
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setAssignResult(null);
  };

  const assignManagerToTeam = async (teamId, managerId = null) => {
    const payload = {
      team_id: Number(teamId),
      selected_manager_id: managerId,
      first_name: managerData.first_name.trim(),
      last_name: managerData.last_name.trim(),
      email: managerData.email.trim(),
      phone: managerData.phone.trim(),
      manager_type: CLUB_SECRETARY_ROLE,
    };

    const response = await api.post(
      `${process.env.REACT_APP_API_URL}/admin/assign_manager_to_team.php`,
      payload
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to assign club secretary');
    }

    return response.data;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!secretaryReady) {
      showError('Please search for or enter club secretary details first');
      return;
    }

    if (selectedTeamIds.size === 0) {
      showError('Please select at least one team');
      return;
    }

    setIsSubmitting(true);
    setAssignResult(null);

    try {
      let managerId = selectedManager?.manager_id || null;
      const toAdd = [...selectedTeamIds].filter((id) => !initialTeamIds.has(id));
      const toRemove = [...initialTeamIds].filter((id) => !selectedTeamIds.has(id));

      const addedTeams = [];
      const removedTeams = [];
      let lastAssignResult = null;

      for (const teamId of toAdd) {
        const result = await assignManagerToTeam(teamId, managerId);
        if (!managerId && result.manager_id) {
          managerId = result.manager_id;
          setSelectedManager({
            manager_id: managerId,
            first_name: managerData.first_name,
            last_name: managerData.last_name,
            email: managerData.email,
            manager_type: CLUB_SECRETARY_ROLE,
          });
          setUserSearchMode('search');
        }
        const team = teams.find((t) => String(t.team_id) === String(teamId));
        if (team) {
          addedTeams.push(team.team_name);
        }
        lastAssignResult = result;
      }

      for (const teamId of toRemove) {
        const response = await api.post(
          `${process.env.REACT_APP_API_URL}/admin/remove_manager_from_team.php`,
          {
            team_id: Number(teamId),
            manager_id: managerId,
          }
        );
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to remove club secretary from team');
        }
        const team = teams.find((t) => String(t.team_id) === String(teamId));
        if (team) {
          removedTeams.push(team.team_name);
        }
      }

      setInitialTeamIds(new Set(selectedTeamIds));

      const secretaryName =
        `${managerData.first_name || selectedManager?.first_name || ''} ${managerData.last_name || selectedManager?.last_name || ''}`.trim();

      if (addedTeams.length === 0 && removedTeams.length === 0) {
        showSuccess('No changes to save');
      } else {
        const parts = [];
        if (addedTeams.length > 0) {
          parts.push(`assigned to ${addedTeams.join(', ')}`);
        }
        if (removedTeams.length > 0) {
          parts.push(`removed from ${removedTeams.join(', ')}`);
        }
        showSuccess(`${secretaryName} ${parts.join('; ')}`);
      }

      if (lastAssignResult) {
        setAssignResult(lastAssignResult);
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || err.message || 'An error occurred while saving team assignments');
    } finally {
      setIsSubmitting(false);
    }
  };

  const secretaryName = selectedManager
    ? `${selectedManager.first_name} ${selectedManager.last_name}`
    : `${managerData.first_name} ${managerData.last_name}`.trim();

  return (
    <div className="assign-manager-page container">
      <Header />
      <div className="assign-manager-content">
        <button type="button" className="back-link" onClick={() => navigate('/admin')}>
          ← Back to Admin
        </button>
        <h1>Assign Club Secretary</h1>
        <p className="page-intro">
          Search for or create a club secretary, then choose which teams they can manage.
        </p>

        <div className="access-details">
          <h2>Club secretary access</h2>
          <p className="access-intro">
            When logged in, a club secretary has the following access for their assigned teams.
          </p>

          <div className="access-group">
            <h3>Dedicated features</h3>
            <ul>
              <li>
                <strong>View player sign-ups</strong> — a dedicated section from team selection to review submitted registration forms for their assigned teams
              </li>
              <li>
                <strong>Manage coaches</strong> — add or remove coaches and managers for their currently selected team (Coach, Assistant Coach, Team Manager, Manager)
              </li>
            </ul>
          </div>

          <div className="access-group">
            <h3>Standard manager access</h3>
            <ul>
              <li>Admin dashboard — manage players, season management, payments (where applicable), and training (if enabled)</li>
              <li>Dashboard, matches, training, and team selection for assigned teams</li>
              <li>Can be linked to multiple teams</li>
            </ul>
          </div>

          <div className="access-group access-restrictions">
            <h3>Restrictions</h3>
            <ul>
              <li>Cannot assign or remove other club secretaries (super admin only)</li>
              <li>No access to super admin tools (team directory, register team, activity log, Stripe, etc.)</li>
              <li>Only see sign-ups for teams they are assigned to</li>
              <li>Excluded from coach dropout notifications when a player cancels</li>
            </ul>
          </div>
        </div>

        <section className="assign-section">
          <h2>1. Club secretary</h2>
          <div className="manager-selection-tabs">
            <button
              type="button"
              className={`tab-button ${userSearchMode === 'search' ? 'active' : ''}`}
              onClick={() => {
                setUserSearchMode('search');
                clearUserSelection();
              }}
            >
              Search existing
            </button>
            <button
              type="button"
              className={`tab-button ${userSearchMode === 'new' ? 'active' : ''}`}
              onClick={() => {
                setUserSearchMode('new');
                clearUserSelection();
              }}
            >
              Add new
            </button>
          </div>

          {userSearchMode === 'search' && (
            <div className="form-group">
              <label htmlFor="user-search">Search managers</label>
              <div className="search-container">
                <input
                  id="user-search"
                  type="text"
                  value={userSearchTerm}
                  onChange={handleUserSearch}
                  placeholder="Name or email…"
                  className="input-field"
                  disabled={!!selectedManager}
                />
                {selectedManager && (
                  <button type="button" className="clear-btn" onClick={clearUserSelection}>
                    Clear
                  </button>
                )}
              </div>
              {isSearching && <p className="loading-text">Searching…</p>}
              {searchResults.length > 0 && !selectedManager && (
                <div className="search-results">
                  {searchResults.map((manager) => (
                    <button
                      key={manager.manager_id}
                      type="button"
                      className="search-result-item"
                      onClick={() => selectManager(manager)}
                    >
                      <span className="result-title">
                        {manager.first_name} {manager.last_name}
                      </span>
                      <span className="result-meta">
                        {[manager.email, manager.manager_type].filter(Boolean).join(' · ')}
                      </span>
                      {manager.teams && <span className="result-meta">Teams: {manager.teams}</span>}
                    </button>
                  ))}
                </div>
              )}
              {selectedManager && (
                <div className="selected-card">
                  <strong>{selectedManager.first_name} {selectedManager.last_name}</strong>
                  <span className="meta">
                    {[selectedManager.email, selectedManager.manager_type].filter(Boolean).join(' · ')}
                  </span>
                  {selectedManager.manager_type !== CLUB_SECRETARY_ROLE && (
                    <p className="empty-hint">
                      This person will be updated to the Club Secretary role when saved.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {userSearchMode === 'new' && (
            <>
              <div className="form-group">
                <label htmlFor="mf">First name *</label>
                <input
                  id="mf"
                  name="first_name"
                  type="text"
                  value={managerData.first_name}
                  onChange={handleManagerChange}
                  className="input-field"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="ml">Last name *</label>
                <input
                  id="ml"
                  name="last_name"
                  type="text"
                  value={managerData.last_name}
                  onChange={handleManagerChange}
                  className="input-field"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="mp">Mobile *</label>
                <input
                  id="mp"
                  name="phone"
                  type="tel"
                  value={managerData.phone}
                  onChange={handleManagerChange}
                  placeholder="07xxx xxxxxx"
                  className="input-field"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="me">Email (optional)</label>
                <input
                  id="me"
                  name="email"
                  type="email"
                  value={managerData.email}
                  onChange={handleManagerChange}
                  className="input-field"
                />
              </div>
            </>
          )}
        </section>

        {secretaryReady && (
          <section className="assign-section">
            <h2>2. Select teams</h2>
            <p className="empty-hint">
              Choose the teams {secretaryName || 'this person'} should be club secretary for.
            </p>

            {teamsLoading || loadingManagerTeams ? (
              <p className="loading-text">Loading teams…</p>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="team-search">Search teams</label>
                  <input
                    id="team-search"
                    type="text"
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    placeholder="Team name, age group, league…"
                    className="input-field"
                  />
                </div>

                <div className="team-checkbox-list">
                  {filteredTeams.length === 0 ? (
                    <p className="empty-hint">No teams match your search.</p>
                  ) : (
                    filteredTeams.map((team) => {
                      const teamId = String(team.team_id);
                      const isChecked = selectedTeamIds.has(teamId);
                      return (
                        <label key={team.team_id} className={`team-checkbox-item ${isChecked ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleTeamSelection(team.team_id)}
                          />
                          <span className="team-checkbox-content">
                            <span className="result-title">{team.team_name}</span>
                            <span className="result-meta">
                              {[team.type, team.age_group, team.league_name].filter(Boolean).join(' · ')}
                            </span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <p className="empty-hint">
                  {selectedTeamIds.size} team{selectedTeamIds.size === 1 ? '' : 's'} selected
                </p>

                <form onSubmit={handleSave}>
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving…' : 'Save team assignments'}
                  </button>
                </form>

                {assignResult && (
                  <div className="success-panel">
                    <h3>Saved successfully</h3>
                    {assignResult.notification_sent ? (
                      <p>Notification sent{assignResult.notification_method ? ` (${assignResult.notification_method})` : ''}.</p>
                    ) : (
                      <p>No notification was sent (missing or invalid phone).</p>
                    )}
                    {assignResult.login_details && (
                      <div className="login-details">
                        <p>New login details (also sent by SMS if possible):</p>
                        <p>
                          Mobile: <strong>{assignResult.login_details.phone}</strong>
                        </p>
                        <p>
                          Password: <strong>{assignResult.login_details.password}</strong>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default AssignClubSecretary;
